import { treaty } from '@elysiajs/eden';
import type { ApiErrorResponse, ApiResponse, App } from '@pricedrop/server/app';
import { getBaseURL } from './base-url';

export const api = treaty<App>(getBaseURL(), {
  fetch: {
    credentials: 'include',
  },
});

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    value.success === false &&
    'error' in value &&
    typeof (value as ApiErrorResponse).error === 'object' &&
    'code' in (value as ApiErrorResponse).error &&
    'message' in (value as ApiErrorResponse).error
  );
}

export async function fetchApi<T>(
  apiCall: Promise<{
    data: ApiResponse<T> | null;
    error: { status: number; value: unknown } | null;
  }>,
): Promise<T> {
  const { data, error } = await apiCall;

  if (error) {
    // Only trigger the global login-modal when we're in the top frame. Inside
    // an iframe (e.g. extension /embed/* surfaces), the cross-origin parent
    // cannot set a SameSite=Lax cookie on pricedrop.co.il via this modal, so
    // dispatching here would pop an unusable login UI. Embed surfaces handle
    // 401 via their own re-auth channel (useExtensionAuth handshake).
    if (error.status === 401 && typeof window !== 'undefined' && window.top === window) {
      window.dispatchEvent(new Event('auth:required'));
    }

    if (isApiErrorResponse(error.value)) {
      throw new ApiError(
        error.value.error.code,
        error.value.error.message,
        error.status,
        error.value.error.details,
      );
    }
    throw new ApiError('UNKNOWN_ERROR', `Request failed with status ${error.status}`, error.status);
  }

  if (!data) {
    throw new ApiError('NO_RESPONSE', 'No response received from server', 0);
  }

  if (!data.success) {
    throw new ApiError(data.error.code, data.error.message, 0, data.error.details);
  }

  return data.data;
}
